import { Plus, UsersThree } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { CandidateTableView } from "./CandidateTableView";
import { InviteCandidateModal } from "screens/buildings/components/rentalTab/modals/inviteCandidateModal";
import { BuildingDTO } from "shared/types/building";
import { useState } from "react";
import SearchComponent, { SearchMode } from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { Select, SelectItem, Switch } from "@heroui/react";
import { getStatusLabel, type CandidacyStatus } from 'screens/candidacies/candidacyPhaseConfig';

interface CandidateListProps {
    building?: BuildingDTO;
    handleOpenAddContractModal: (contacts?: Array<{ id: number; name: string; isOnContract: boolean; resourceEntityId?: number; wasExisting?: boolean; residentData?: { email?: string | null; phoneNumber?: string | null; spokenLanguages?: string[]; nationality?: string | null; dateOfBirth?: string | null; placeOfBirth?: string | null; relationToApplicant?: string | null } }>, unitId?: string, contractType?: string, totalResidents?: number) => void;
    showArchivedToggle?: boolean;
    // External filters from FilterBar (optional)
    externalStatusFilters?: string[];
    externalSearchText?: string;
    externalBuildingFilter?: string;
    /** Pre-selected unit (e.g. deep-linked from leegstand table) */
    externalUnitFilter?: string;
    externalIncludeArchived?: boolean; // Controlled from outside (e.g. FilterBar)
    /** Verberg kandidaten van units met verhuurstatus "Verhuurd" (FilterBar chip) */
    externalExcludeRentedUnits?: boolean;
    hideInternalFilters?: boolean; // Hide the internal filter UI when using external filters
    onCandidateClick?: (candidate: any) => void; // Optional external click handler
}

export const CandidateList = ({
    building,
    handleOpenAddContractModal,
    showArchivedToggle = false,
    externalStatusFilters,
    externalSearchText,
    externalBuildingFilter,
    externalUnitFilter,
    externalIncludeArchived,
    externalExcludeRentedUnits,
    hideInternalFilters = false,
    onCandidateClick
}: CandidateListProps) => {

    const [isInviteCandidateModalOpen, setIsInviteCandidateModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        unit: "",
        building: "",
        status: ""
    });
    const [internalIncludeArchived, setInternalIncludeArchived] = useState(false);
    const includeArchived = externalIncludeArchived ?? internalIncludeArchived;

    const handleUnitSelect = (unit: any) => {
        setFilters(prev => ({ ...prev, unit: unit?.resourceId || "" }));
    };

    const handleBuildingSelect = (selectedBuilding: any) => {
        setFilters(prev => ({ ...prev, building: selectedBuilding?.resourceId || "" }));
    };

    const handleStatusChange = (value: string) => {
        setFilters(prev => ({ ...prev, status: value }));
    };

    const statusOptions = [
        { value: "", label: "Alle statussen" },
        // 🟠 We need to act (Orange)
        { value: "interested", label: getStatusLabel('interested' as CandidacyStatus) },
        { value: "notify", label: getStatusLabel('notify' as CandidacyStatus) },
        // 🔵 They need to act (Blue)
        { value: "invited", label: getStatusLabel('invited' as CandidacyStatus) },
        { value: "reminder-sent", label: getStatusLabel('reminder-sent' as CandidacyStatus) },
        { value: "awaiting-visit", label: getStatusLabel('awaiting-visit' as CandidacyStatus) },
        { value: "awaiting-alternate-selection", label: getStatusLabel('awaiting-alternate-selection' as CandidacyStatus) },
        { value: "awaiting-interested", label: getStatusLabel('awaiting-interested' as CandidacyStatus) },
        { value: "forwarded-to-broker", label: getStatusLabel('forwarded-to-broker' as CandidacyStatus) },
        // ✅ Positive completion (Green)
        { value: "approved", label: getStatusLabel('approved' as CandidacyStatus) },
        { value: "tenant", label: getStatusLabel('tenant' as CandidacyStatus) },
        // ❌ Negative completion (Red)
        { value: "rejected", label: getStatusLabel('rejected' as CandidacyStatus) },
        { value: "not-interested", label: getStatusLabel('not-interested' as CandidacyStatus) },
    ];

    // Merge internal and external filters
    const mergedFilters = {
        ...filters,
        unit: externalUnitFilter || filters.unit,
        building: externalBuildingFilter || filters.building,
        statusArray: externalStatusFilters,
        searchText: externalSearchText,
        excludeRentedUnits: externalExcludeRentedUnits,
    };
    
    return (
        <>
            {building && (
              <header className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-lg font-medium">
                  <UsersThree weight="duotone" size={20} /> Kandidaten
                </div>
                <div className="flex gap-4">
                  <ButtonNew
                    onClick={() => setIsInviteCandidateModalOpen(true)}
                    icon={<Plus />}
                    label="Kandidaat uitnodigen"
                    variant="primaryMoss"
                  />
                </div>
              </header>
            )}

          {/* Filters Section - Only show when not using external filters */}
          {!hideInternalFilters && (
            <div className="">
              {/* Filter Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-primary">Unit</label>
                    <SearchComponent
                      type="regular"
                      label="Zoek unit"
                      resourceFilter={[ResourceType.UNIT]}
                      onSelect={handleUnitSelect}
                      mode={SearchMode.SEARCH_AND_PRESENT}
                      hasEditToggle={true}
                      editMode={true}
                    />
                  </div>

                  {!building && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-neutral-primary">Gebouw</label>
                      <SearchComponent
                        type="regular"
                        label="Zoek gebouw"
                        resourceFilter={[ResourceType.BUILDING]}
                        onSelect={handleBuildingSelect}
                        mode={SearchMode.SEARCH_AND_PRESENT}
                        hasEditToggle={true}
                        editMode={true}
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-primary">Status</label>
                    <Select
                      selectedKeys={filters.status ? [filters.status] : []}
                      onSelectionChange={(keys) => {
                        const selectedKey = Array.from(keys as Set<string>)[0];
                        handleStatusChange(selectedKey || "");
                      }}
                      placeholder="Selecteer status"
                      classNames={{
                        label: "text-neutral-primary",
                        value: "text-neutral-primary",
                        innerWrapper: "h-fit",
                        trigger:
                          "rounded-lg bg-white h-7 border border-neutral-primary/20 hover:border-neutral-primary focus:border-moss-primary data-[hover=true]:bg-white group-data-[focus=true]:bg-white transition-colors",
                        popoverContent: "rounded-lg border border-neutral-primary/20",
                        listbox: "p-0",
                      }}
                    >
                      {statusOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          classNames={{
                            base: "hover:bg-moss-primary/10 data-[selected=true]:bg-moss-primary data-[selected=true]:text-white",
                          }}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
              </div>
            </div>
          )}

          {showArchivedToggle && externalIncludeArchived === undefined && (
            <div className="flex items-center gap-2 mb-2">
              <Switch
                isSelected={internalIncludeArchived}
                onValueChange={setInternalIncludeArchived}
                size="sm"
              />
              <label className="text-sm font-medium text-neutral-primary">
                Toon alleen gearchiveerde kandidaturen
              </label>
            </div>
          )}

          <CandidateTableView
            buildingId={building?.id as string}
            handleOpenAddContractModal={handleOpenAddContractModal}
            filters={mergedFilters}
            includeArchived={includeArchived}
            onCandidateClick={onCandidateClick}
          />
          <InviteCandidateModal
                isOpen={isInviteCandidateModalOpen}
                onClose={() => setIsInviteCandidateModalOpen(false)}
                units={building?.units}
            />
        </>
    )
}